import * as THREE from "three";

export type MonsterKind =
  | "slime"
  | "bat"
  | "skeleton"
  | "ghost"
  | "golem"
  | "wolf"
  | "plant"
  | "knight"
  | "dragon";

export interface MonsterAppearance {
  kind: MonsterKind;
  color: number;
  accent?: number;
  /** Uniform scale. 1 puts the creature at roughly one tile tall. */
  scale?: number;
}

function mat(color: number, opts: THREE.MeshLambertMaterialParameters = {}): THREE.MeshLambertMaterial {
  return new THREE.MeshLambertMaterial({ color, ...opts });
}

function box(w: number, h: number, d: number, material: THREE.Material): THREE.Mesh {
  const mesh = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), material);
  mesh.castShadow = true;
  return mesh;
}

function sphere(r: number, material: THREE.Material, widthSeg = 12, heightSeg = 9): THREE.Mesh {
  const mesh = new THREE.Mesh(new THREE.SphereGeometry(r, widthSeg, heightSeg), material);
  mesh.castShadow = true;
  return mesh;
}

function eyes(parent: THREE.Object3D, spread: number, y: number, z: number, size = 0.06): void {
  const white = mat(0xffffff);
  const pupil = mat(0x101018);
  for (const side of [-1, 1]) {
    const eye = new THREE.Mesh(new THREE.SphereGeometry(size, 8, 6), white);
    eye.position.set(side * spread, y, z);
    eye.scale.z = 0.6;
    const dot = new THREE.Mesh(new THREE.SphereGeometry(size * 0.5, 6, 5), pupil);
    dot.position.set(side * spread, y, z + size * 0.55);
    parent.add(eye, dot);
  }
}

type IdleStyle = "wobble" | "hover" | "sway" | "stomp" | "prowl";

/**
 * Procedural monster. Every creature in the bestiary is described by a kind and
 * two colours, so adding a monster is a data change, not a modelling session.
 */
export class MonsterModel {
  readonly object3D = new THREE.Group();
  /** Approximate height in world units, for placing damage popups. */
  height = 1;

  private readonly body = new THREE.Group();
  private readonly materials: THREE.MeshLambertMaterial[] = [];
  private readonly baseEmissive: number[] = [];
  private readonly wings: THREE.Object3D[] = [];
  private idleStyle: IdleStyle = "wobble";
  private phase = Math.random() * Math.PI * 2;
  private flashTimer = 0;

  constructor(appearance: MonsterAppearance) {
    const { kind, color } = appearance;
    const accent = appearance.accent ?? 0xffffff;
    this.object3D.add(this.body);
    this.object3D.scale.setScalar(appearance.scale ?? 1);

    switch (kind) {
      case "slime":
        this.buildSlime(color, accent);
        break;
      case "bat":
        this.buildBat(color, accent);
        break;
      case "skeleton":
        this.buildSkeleton(color, accent);
        break;
      case "ghost":
        this.buildGhost(color, accent);
        break;
      case "golem":
        this.buildGolem(color, accent);
        break;
      case "wolf":
        this.buildWolf(color, accent);
        break;
      case "plant":
        this.buildPlant(color, accent);
        break;
      case "knight":
        this.buildKnight(color, accent);
        break;
      case "dragon":
        this.buildDragon(color, accent);
        break;
    }

    // Cache materials so the whole creature can flash white when it is hit.
    this.object3D.traverse((obj) => {
      const m = (obj as THREE.Mesh).material;
      const list = Array.isArray(m) ? m : m ? [m] : [];
      for (const item of list) {
        if (item instanceof THREE.MeshLambertMaterial && !this.materials.includes(item)) {
          this.materials.push(item);
          this.baseEmissive.push(item.emissive.getHex());
        }
      }
    });
  }

  // --------------------------------------------------------------- builders

  private buildSlime(color: number, accent: number): void {
    const blob = sphere(0.42, mat(color, { emissive: color, emissiveIntensity: 0.12 }), 16, 12);
    blob.scale.set(1.15, 0.85, 1.05);
    blob.position.y = 0.33;
    this.body.add(blob);
    eyes(this.body, 0.13, 0.42, 0.36, 0.07);
    const mouth = box(0.14, 0.03, 0.02, mat(accent));
    mouth.position.set(0, 0.26, 0.4);
    this.body.add(mouth);
    this.idleStyle = "wobble";
    this.height = 0.75;
  }

  private buildBat(color: number, accent: number): void {
    const torso = sphere(0.22, mat(color));
    torso.position.y = 0.8;
    torso.scale.set(1, 1.1, 0.9);
    this.body.add(torso);
    eyes(this.body, 0.09, 0.85, 0.19, 0.05);

    for (const side of [-1, 1]) {
      const ear = new THREE.Mesh(new THREE.ConeGeometry(0.06, 0.16, 4), mat(color));
      ear.position.set(side * 0.1, 1.02, 0);
      this.body.add(ear);

      const wing = new THREE.Group();
      const membrane = new THREE.Mesh(
        new THREE.PlaneGeometry(0.42, 0.26),
        new THREE.MeshLambertMaterial({ color: accent, side: THREE.DoubleSide }),
      );
      membrane.position.x = side * 0.22;
      wing.add(membrane);
      wing.position.set(side * 0.16, 0.85, 0);
      this.body.add(wing);
      this.wings.push(wing);
    }
    this.idleStyle = "hover";
    this.height = 1.1;
  }

  private buildSkeleton(color: number, accent: number): void {
    const bone = mat(color);
    const pelvis = box(0.22, 0.1, 0.14, bone);
    pelvis.position.y = 0.46;
    const ribs = box(0.26, 0.26, 0.16, bone);
    ribs.position.y = 0.66;
    const skull = box(0.22, 0.2, 0.2, bone);
    skull.position.y = 0.92;
    this.body.add(pelvis, ribs, skull);

    for (const side of [-1, 1]) {
      const socket = box(0.06, 0.06, 0.02, mat(0x101018));
      socket.position.set(side * 0.055, 0.94, 0.1);
      const leg = box(0.07, 0.42, 0.07, bone);
      leg.position.set(side * 0.08, 0.22, 0);
      const arm = box(0.06, 0.34, 0.06, bone);
      arm.position.set(side * 0.19, 0.62, 0);
      this.body.add(socket, leg, arm);
    }

    const blade = box(0.05, 0.44, 0.05, mat(accent));
    blade.position.set(0.19, 0.34, 0.06);
    this.body.add(blade);
    this.idleStyle = "sway";
    this.height = 1.05;
  }

  private buildGhost(color: number, accent: number): void {
    const skin = new THREE.MeshLambertMaterial({
      color,
      transparent: true,
      opacity: 0.7,
      emissive: color,
      emissiveIntensity: 0.25,
    });
    const head = sphere(0.28, skin, 14, 10);
    head.position.y = 0.9;
    const shroud = new THREE.Mesh(new THREE.ConeGeometry(0.34, 0.7, 12), skin);
    shroud.position.y = 0.52;
    shroud.castShadow = true;
    this.body.add(head, shroud);

    for (const side of [-1, 1]) {
      const socket = new THREE.Mesh(new THREE.SphereGeometry(0.07, 8, 6), mat(accent, { emissive: accent }));
      socket.position.set(side * 0.11, 0.94, 0.21);
      const arm = new THREE.Mesh(new THREE.CapsuleGeometry(0.05, 0.2, 4, 6), skin);
      arm.position.set(side * 0.3, 0.72, 0.05);
      arm.rotation.z = side * 0.5;
      this.body.add(socket, arm);
    }
    this.idleStyle = "hover";
    this.height = 1.2;
  }

  private buildGolem(color: number, accent: number): void {
    const stone = mat(color);
    const torso = box(0.62, 0.5, 0.4, stone);
    torso.position.y = 0.72;
    const head = box(0.3, 0.26, 0.28, stone);
    head.position.y = 1.1;
    this.body.add(torso, head);
    eyes(this.body, 0.09, 1.12, 0.15, 0.05);

    for (const side of [-1, 1]) {
      const arm = box(0.18, 0.5, 0.2, stone);
      arm.position.set(side * 0.42, 0.68, 0);
      const fist = box(0.24, 0.2, 0.24, stone);
      fist.position.set(side * 0.42, 0.4, 0);
      const leg = box(0.22, 0.46, 0.24, stone);
      leg.position.set(side * 0.16, 0.24, 0);
      this.body.add(arm, fist, leg);
    }

    const core = sphere(0.09, mat(accent, { emissive: accent, emissiveIntensity: 0.6 }), 8, 6);
    core.position.set(0, 0.78, 0.21);
    this.body.add(core);
    this.idleStyle = "stomp";
    this.height = 1.3;
  }

  private buildWolf(color: number, accent: number): void {
    const fur = mat(color);
    const torso = box(0.34, 0.3, 0.62, fur);
    torso.position.y = 0.44;
    const neck = box(0.26, 0.24, 0.2, fur);
    neck.position.set(0, 0.56, 0.3);
    const snout = box(0.16, 0.14, 0.2, mat(accent));
    snout.position.set(0, 0.5, 0.5);
    this.body.add(torso, neck, snout);
    eyes(this.body, 0.09, 0.62, 0.4, 0.05);

    for (const side of [-1, 1]) {
      const ear = new THREE.Mesh(new THREE.ConeGeometry(0.07, 0.14, 4), fur);
      ear.position.set(side * 0.1, 0.72, 0.28);
      this.body.add(ear);
      for (const z of [0.22, -0.22]) {
        const leg = box(0.11, 0.32, 0.12, fur);
        leg.position.set(side * 0.12, 0.16, z);
        this.body.add(leg);
      }
    }

    const tail = box(0.1, 0.1, 0.3, fur);
    tail.position.set(0, 0.54, -0.36);
    tail.rotation.x = -0.5;
    this.body.add(tail);
    this.idleStyle = "prowl";
    this.height = 0.85;
  }

  private buildPlant(color: number, accent: number): void {
    const stem = new THREE.Mesh(new THREE.CylinderGeometry(0.07, 0.11, 0.6, 8), mat(0x3f7a30));
    stem.position.y = 0.3;
    stem.castShadow = true;
    const bulb = sphere(0.27, mat(color), 14, 10);
    bulb.position.y = 0.72;
    this.body.add(stem, bulb);

    // Petals fanned around the head.
    for (let i = 0; i < 6; i++) {
      const angle = (i / 6) * Math.PI * 2;
      const petal = new THREE.Mesh(new THREE.ConeGeometry(0.1, 0.28, 5), mat(accent));
      petal.position.set(Math.cos(angle) * 0.28, 0.78, Math.sin(angle) * 0.28);
      petal.rotation.z = -Math.cos(angle) * 1.1;
      petal.rotation.x = Math.sin(angle) * 1.1;
      petal.castShadow = true;
      this.body.add(petal);
    }

    const maw = box(0.24, 0.06, 0.06, mat(0x3a0d18));
    maw.position.set(0, 0.68, 0.25);
    this.body.add(maw);
    for (let i = -1; i <= 1; i++) {
      const tooth = new THREE.Mesh(new THREE.ConeGeometry(0.025, 0.07, 4), mat(0xfff4e0));
      tooth.position.set(i * 0.08, 0.72, 0.26);
      tooth.rotation.x = Math.PI;
      this.body.add(tooth);
    }
    eyes(this.body, 0.12, 0.84, 0.2, 0.05);
    this.idleStyle = "sway";
    this.height = 1.0;
  }

  private buildKnight(color: number, accent: number): void {
    const armor = mat(color, { emissive: color, emissiveIntensity: 0.08 });
    const trim = mat(accent);
    const torso = box(0.38, 0.36, 0.24, armor);
    torso.position.y = 0.62;
    const skirt = box(0.42, 0.18, 0.28, armor);
    skirt.position.y = 0.38;
    this.body.add(torso, skirt);

    const helm = box(0.26, 0.26, 0.26, armor);
    helm.position.y = 0.94;
    const visor = box(0.2, 0.05, 0.02, mat(0x101018));
    visor.position.set(0, 0.94, 0.14);
    const plume = box(0.05, 0.14, 0.22, trim);
    plume.position.y = 1.13;
    this.body.add(helm, visor, plume);

    for (const side of [-1, 1]) {
      const pauldron = box(0.16, 0.14, 0.24, trim);
      pauldron.position.set(side * 0.27, 0.75, 0);
      const arm = box(0.11, 0.3, 0.13, armor);
      arm.position.set(side * 0.27, 0.55, 0);
      const leg = box(0.14, 0.34, 0.16, armor);
      leg.position.set(side * 0.11, 0.17, 0);
      this.body.add(pauldron, arm, leg);
    }

    const lance = box(0.05, 0.7, 0.05, trim);
    lance.position.set(0.27, 0.5, 0.1);
    lance.rotation.x = 0.25;
    this.body.add(lance);
    this.idleStyle = "sway";
    this.height = 1.25;
  }

  private buildDragon(color: number, accent: number): void {
    const hide = mat(color);
    const belly = mat(accent);

    const torso = sphere(0.4, hide, 14, 10);
    torso.scale.set(1, 0.9, 1.25);
    torso.position.y = 0.7;
    this.body.add(torso);

    const neck = new THREE.Mesh(new THREE.CylinderGeometry(0.13, 0.18, 0.5, 8), hide);
    neck.position.set(0, 1.05, 0.28);
    neck.rotation.x = 0.55;
    neck.castShadow = true;
    const head = box(0.28, 0.24, 0.4, hide);
    head.position.set(0, 1.32, 0.52);
    const jaw = box(0.24, 0.08, 0.34, belly);
    jaw.position.set(0, 1.19, 0.56);
    this.body.add(neck, head, jaw);
    eyes(this.body, 0.1, 1.38, 0.66, 0.05);

    for (const side of [-1, 1]) {
      const horn = new THREE.Mesh(new THREE.ConeGeometry(0.05, 0.22, 5), belly);
      horn.position.set(side * 0.1, 1.48, 0.44);
      horn.rotation.x = -0.5;
      this.body.add(horn);

      const wing = new THREE.Group();
      const membrane = new THREE.Mesh(
        new THREE.PlaneGeometry(0.7, 0.5),
        new THREE.MeshLambertMaterial({ color: accent, side: THREE.DoubleSide }),
      );
      membrane.position.x = side * 0.36;
      wing.add(membrane);
      wing.position.set(side * 0.28, 0.95, -0.05);
      wing.rotation.y = side * 0.3;
      this.body.add(wing);
      this.wings.push(wing);

      const leg = box(0.16, 0.34, 0.2, hide);
      leg.position.set(side * 0.24, 0.2, 0.1);
      this.body.add(leg);
    }

    const tail = new THREE.Mesh(new THREE.ConeGeometry(0.16, 0.8, 8), hide);
    tail.position.set(0, 0.6, -0.6);
    tail.rotation.x = Math.PI / 2 - 0.2;
    tail.castShadow = true;
    this.body.add(tail);
    this.idleStyle = "hover";
    this.height = 1.6;
  }

  // -------------------------------------------------------------- animation

  update(dt: number): void {
    this.phase += dt;
    const t = this.phase;

    switch (this.idleStyle) {
      case "wobble": {
        const squash = 1 + Math.sin(t * 3.4) * 0.07;
        this.body.scale.set(1 / Math.sqrt(squash), squash, 1 / Math.sqrt(squash));
        break;
      }
      case "hover":
        this.body.position.y = Math.sin(t * 2.6) * 0.09;
        for (const [i, wing] of this.wings.entries()) {
          const side = i === 0 ? -1 : 1;
          wing.rotation.z = side * (0.3 + Math.sin(t * 7) * 0.55);
        }
        break;
      case "sway":
        this.body.rotation.z = Math.sin(t * 1.8) * 0.06;
        this.body.position.y = Math.abs(Math.sin(t * 1.8)) * 0.02;
        break;
      case "stomp":
        this.body.position.y = Math.abs(Math.sin(t * 1.4)) * 0.05;
        this.body.rotation.y = Math.sin(t * 0.7) * 0.08;
        break;
      case "prowl":
        this.body.position.z = Math.sin(t * 2.2) * 0.04;
        this.body.rotation.x = Math.sin(t * 2.2) * 0.04;
        break;
    }

    if (this.flashTimer > 0) {
      this.flashTimer = Math.max(0, this.flashTimer - dt);
      const lit = this.flashTimer > 0;
      this.materials.forEach((m, i) => {
        m.emissive.setHex(lit ? 0xff4444 : this.baseEmissive[i]);
        m.emissiveIntensity = lit ? 0.9 : 1;
      });
    }
  }

  /** Tint the creature red for a moment — the universal "that hurt". */
  flash(seconds = 0.18): void {
    this.flashTimer = seconds;
  }

  setOpacity(value: number): void {
    for (const m of this.materials) {
      m.transparent = true;
      m.opacity = value;
    }
  }

  dispose(): void {
    this.object3D.traverse((obj) => {
      const mesh = obj as Partial<THREE.Mesh>;
      mesh.geometry?.dispose();
      const m = mesh.material;
      if (Array.isArray(m)) m.forEach((x) => x.dispose());
      else m?.dispose();
    });
  }
}
